// Ported from obsidian-plugin/src/components/ChatInput.tsx.
// Obsidian adaptations:
//   - Removed [[wikilink]] autocomplete + vault reference chips.
//     No vault in the browser; users just type plain text.
//   - Removed RagOptionsPopup (plugin settings UI) — replaced with a small
//     inline popover that edits ChatOptions kept in local state.
//   - Removed Graph / Timeline panel toggles (deferred for v2).
//   - Removed `useApp()` hook usage.
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  KeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChatOptions, ChatMode, RetrievalProfile } from '../../types';
import Stepper from '../../components/Stepper';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled: boolean;
  streaming?: boolean;
  onStop?: () => void;
  useRag: boolean;
  onToggleRag: () => void;
  chatOptions: ChatOptions;
  onChatOptionsChange: (partial: Partial<ChatOptions>) => void;
  estimatedTokens?: number;
  maxContextTokens?: number;
  graphOpen?: boolean;
  onToggleGraph?: () => void;
  hasUnseenGraphData?: boolean;
  subgraphScope?: 'none' | 'search_within' | 'expand_from';
  pinnedNodeCount?: number;
  llmStatus?: string;
}

function formatTokens(n: number): string {
  if (n >= 1000) return `~${(n / 1000).toFixed(1)}k`;
  return `~${n}`;
}

function ContextRing({ tokens, maxTokens }: { tokens: number; maxTokens: number }) {
  const pct = Math.min(tokens / maxTokens, 1);
  const r = 9;
  const cx = 11;
  const cy = 11;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct);

  const strokeColor =
    pct < 0.5
      ? 'var(--color-green)'
      : pct < 0.75
        ? 'var(--color-yellow)'
        : pct < 0.9
          ? 'var(--color-orange)'
          : 'var(--color-red)';

  const tooltipText = `${formatTokens(tokens)} / ${formatTokens(maxTokens)} tokens (${Math.round(pct * 100)}%)`;

  return (
    <div className="malpedia-context-ring-wrap">
      <svg
        className="malpedia-context-ring"
        width="22"
        height="22"
        viewBox="0 0 22 22"
        aria-label={tooltipText}
      >
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth="3"
          className="malpedia-context-ring-track"
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={strokeColor}
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>
      <div className="malpedia-context-ring-tooltip">{tooltipText}</div>
    </div>
  );
}

export interface ChatInputHandle {
  setValue: (text: string) => void;
  focus: () => void;
}

// ── User-friendly naming maps ──────────────────────────────────────────

const MODE_LABELS: Record<ChatMode, string> = {
  auto: 'Auto',
  vector: 'Semantic',
  local: 'Connected',
  global: 'Entity',
  drift: 'Discovery',
  planned: 'Planned',
  llm_only: 'Chat only',
};

const MODE_DESCRIPTIONS: Record<ChatMode, string> = {
  auto: 'Analyzes your question and picks the best approach',
  vector: 'Finds passages by meaning similarity and keywords',
  local: 'Follows entity relationships from text matches',
  global: 'Looks up threat actors and malware by name',
  drift: 'Explores outward to discover unexpected connections',
  planned: 'AI planner combines multiple search approaches',
  llm_only: 'No document search — conversation only',
};

const SIMPLE_MODES: ChatMode[] = ['auto', 'vector', 'global', 'llm_only'];
const ALL_MODES: ChatMode[] = ['auto', 'vector', 'local', 'global', 'drift', 'planned', 'llm_only'];

type DepthPreset = 'auto' | 'quick' | 'balanced' | 'thorough' | 'deep' | 'custom';

const DEPTH_LABELS: Record<DepthPreset, string> = {
  auto: 'Auto',
  quick: 'Quick',
  balanced: 'Balanced',
  thorough: 'Thorough',
  deep: 'Deep',
  custom: 'Custom',
};

const DEPTH_TO_API: Record<DepthPreset, RetrievalProfile> = {
  auto: 'auto',
  quick: 'narrow',
  balanced: 'normal',
  thorough: 'broad',
  deep: 'exhaustive',
  custom: 'manual',
};

const API_TO_DEPTH: Record<RetrievalProfile, DepthPreset> = {
  auto: 'auto',
  narrow: 'quick',
  normal: 'balanced',
  broad: 'thorough',
  exhaustive: 'deep',
  manual: 'custom',
};

const DEPTH_DESCRIPTIONS: Record<DepthPreset, string> = {
  auto: 'System decides based on question complexity',
  quick: '~4 passages — fast lookups, specific entities',
  balanced: '~8 passages — good for most questions',
  thorough: '~20 passages — complex topics, comparisons',
  deep: '~50 passages — research, campaign analysis',
  custom: 'You control all parameters',
};

const PROFILE_VALUES: Record<string, { topK: number; topSources: number; fusionCap: number; perReport: number }> = {
  quick:    { topK: 5,  topSources: 4,  fusionCap: 10,  perReport: 5 },
  balanced: { topK: 10, topSources: 8,  fusionCap: 20,  perReport: 5 },
  thorough: { topK: 25, topSources: 20, fusionCap: 50,  perReport: 3 },
  deep:     { topK: 50, topSources: 50, fusionCap: 150, perReport: 3 },
};

const SIMPLE_DEPTHS: DepthPreset[] = ['auto', 'quick', 'balanced', 'thorough', 'deep'];
const ALL_DEPTHS: DepthPreset[] = ['auto', 'quick', 'balanced', 'thorough', 'deep', 'custom'];

/** Inline toggle switch — hidden checkbox + styled track/thumb. */
function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="malpedia-rag-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="malpedia-rag-toggle-track" />
    </label>
  );
}

/** Layer 1 / Layer 2 options popover — replaces the Obsidian RagOptionsPopup. */
function OptionsPopover({
  options,
  onChange,
  onClose,
}: {
  options: ChatOptions;
  onChange: (partial: Partial<ChatOptions>) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [advanced, setAdvanced] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on click outside (parent wrap includes the trigger button)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const wrap = popupRef.current?.parentElement;
      if (wrap && !wrap.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const currentDepth = API_TO_DEPTH[options.retrievalProfile] ?? 'auto';
  const modes = advanced ? ALL_MODES : SIMPLE_MODES;
  const depths = advanced ? ALL_DEPTHS : SIMPLE_DEPTHS;
  const profileVals = PROFILE_VALUES[currentDepth];

  const handleDepthChange = (d: DepthPreset) => {
    onChange({ retrievalProfile: DEPTH_TO_API[d] });
  };

  return (
    <div
      className="malpedia-rag-options-popup"
      ref={popupRef}
      role="dialog"
      aria-label="RAG options"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      tabIndex={-1}
    >
      {/* Strategy */}
      <div className="malpedia-rag-option-row">
        <span>Strategy</span>
        <select
          className="malpedia-rag-select"
          value={options.mode}
          title={MODE_DESCRIPTIONS[options.mode]}
          onChange={(e) => onChange({ mode: e.target.value as ChatMode })}
        >
          {modes.map((m) => (
            <option key={m} value={m}>{MODE_LABELS[m]}</option>
          ))}
        </select>
      </div>

      <div className="malpedia-rag-separator" />

      {/* Depth */}
      <div className="malpedia-rag-option-row">
        <span>Depth</span>
        <select
          className="malpedia-rag-select"
          value={currentDepth}
          title={DEPTH_DESCRIPTIONS[currentDepth]}
          onChange={(e) => handleDepthChange(e.target.value as DepthPreset)}
        >
          {depths.map((d) => (
            <option key={d} value={d}>{DEPTH_LABELS[d]}</option>
          ))}
        </select>
      </div>

      {/* Profile hint (hidden when custom — steppers replace it) */}
      {currentDepth !== 'custom' && (
        <div className="malpedia-rag-hint">
          {profileVals
            ? `${profileVals.topK} candidates · ${profileVals.topSources} context · ${profileVals.fusionCap} merge`
            : DEPTH_DESCRIPTIONS[currentDepth]}
        </div>
      )}

      {/* Custom depth steppers (advanced + custom only) */}
      {advanced && currentDepth === 'custom' && (
        <>
          <div className="malpedia-rag-section-label">Custom parameters</div>
          <div className="malpedia-rag-option-row">
            <span>Candidates</span>
            <Stepper value={options.topK} onChange={(v) => onChange({ topK: v })} min={1} max={200} step={5} />
          </div>
          <div className="malpedia-rag-option-row">
            <span>Context size</span>
            <Stepper value={options.customTopSources} onChange={(v) => onChange({ customTopSources: v })} min={1} max={100} step={5} />
          </div>
          <div className="malpedia-rag-option-row">
            <span>Merge limit</span>
            <Stepper value={options.customFusionCap} onChange={(v) => onChange({ customFusionCap: v })} min={5} max={500} step={10} />
          </div>
          <div className="malpedia-rag-option-row">
            <span>Per report</span>
            <Stepper value={options.customMaxChunksPerDoc} onChange={(v) => onChange({ customMaxChunksPerDoc: v })} min={1} max={10} step={1} />
          </div>
        </>
      )}

      <div className="malpedia-rag-separator" />

      {/* Toggles */}
      <div className="malpedia-rag-option-row">
        <span>IOC search</span>
        <ToggleSwitch checked={options.iocPivot} onChange={(v) => onChange({ iocPivot: v })} />
      </div>
      <div className="malpedia-rag-option-row">
        <span>Web search</span>
        <ToggleSwitch checked={options.webSearch} onChange={(v) => onChange({ webSearch: v })} />
      </div>

      {/* Advanced: context enrichment toggles */}
      {advanced && (
        <>
          <div className="malpedia-rag-separator" />
          <div className="malpedia-rag-section-label">Context enrichment</div>
          <div className="malpedia-rag-option-row">
            <span>Expand context</span>
            <ToggleSwitch
              checked={options.neighborExpansion}
              onChange={(v) => onChange({
                neighborExpansion: v,
                ...(v && { fullDocContext: false }),
              })}
            />
          </div>
          <div className="malpedia-rag-option-row">
            <span>Full document</span>
            <ToggleSwitch
              checked={options.fullDocContext}
              onChange={(v) => onChange({
                fullDocContext: v,
                ...(v && { neighborExpansion: false }),
              })}
            />
          </div>
          <div className="malpedia-rag-option-row">
            <span>v2 IOC graph (experimental)</span>
            <ToggleSwitch
              checked={options.useV2IocGraph}
              onChange={(v) => onChange({ useV2IocGraph: v })}
            />
          </div>
        </>
      )}

      <div className="malpedia-rag-separator" />

      {/* Footer */}
      <div className="malpedia-rag-footer">
        <button
          type="button"
          className="malpedia-rag-footer-link"
          onClick={() => setAdvanced((v) => !v)}
        >
          {advanced ? '‹ Simple' : 'Advanced ›'}
        </button>
        <button
          type="button"
          className="malpedia-rag-footer-link accent"
          onClick={() => { onClose(); navigate('/settings'); }}
        >
          Full settings →
        </button>
      </div>
    </div>
  );
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  {
    onSend,
    disabled,
    streaming,
    onStop,
    useRag,
    onToggleRag,
    chatOptions,
    onChatOptionsChange,
    estimatedTokens,
    maxContextTokens,
    graphOpen,
    onToggleGraph,
    hasUnseenGraphData,
    subgraphScope,
    pinnedNodeCount,
    llmStatus,
  },
  ref,
) {
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const llmBlocked = !!llmStatus && llmStatus !== 'ok';
  const llmBannerText =
    llmStatus === 'not_configured'
      ? 'No LLM backend configured.'
      : llmBlocked
        ? `LLM unavailable: ${llmStatus!.replace(/^unreachable:\s*/, '')}`
        : '';

  useImperativeHandle(
    ref,
    () => ({
      setValue: (text: string) => {
        setInputValue(text);
        requestAnimationFrame(() => textareaRef.current?.focus());
      },
      focus: () => textareaRef.current?.focus(),
    }),
    [],
  );

  const handleSend = useCallback(() => {
    const value = inputValue.trim();
    if (!value || disabled) return;
    onSend(value);
    setInputValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [inputValue, disabled, onSend]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    handleInput();
  };

  return (
    <div className="malpedia-input-area">
      <div className="malpedia-input-card">
        {llmBlocked && (
          <div
            className="malpedia-input-llm-warning"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              marginBottom: '0.5rem',
              borderRadius: '0.5rem',
              backgroundColor: 'var(--background-modifier-warning, #fff3cd)',
              color: 'var(--text-warning, #856404)',
              fontSize: '0.85rem',
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>&#x26A0;</span>
            <span style={{ flex: 1 }}>{llmBannerText}</span>
            <button
              type="button"
              className="malpedia-rag-footer-link accent"
              onClick={() => navigate('/settings')}
            >
              Go to Settings →
            </button>
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="malpedia-chat-input"
          placeholder={
            llmBlocked
              ? 'LLM not configured — see Settings'
              : 'Ask about threat actors, malware families… (Enter to send, Shift+Enter for newline)'
          }
          value={inputValue}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          disabled={llmBlocked}
          rows={1}
        />
        <div className="malpedia-input-toolbar">
          <div className="malpedia-input-toolbar-left">
            <div className="malpedia-input-toggle-pill">
              <button
                className={`malpedia-input-toggle-btn${useRag ? ' active' : ''}`}
                onClick={useRag ? undefined : onToggleRag}
                title="Search threat reports"
              >
                RAG
              </button>
              <button
                className={`malpedia-input-toggle-btn${!useRag ? ' active' : ''}`}
                onClick={useRag ? onToggleRag : undefined}
                title="Plain conversation"
              >
                Chat
              </button>
            </div>
            {useRag && (
              <div className="malpedia-input-options-wrap">
                <button
                  className={`malpedia-input-options-btn${showPopup ? ' active' : ''}`}
                  onClick={() => setShowPopup((v) => !v)}
                  title="RAG options"
                >
                  •••
                </button>
                {showPopup && (
                  <OptionsPopover
                    options={chatOptions}
                    onChange={onChatOptionsChange}
                    onClose={() => setShowPopup(false)}
                  />
                )}
              </div>
            )}
            {onToggleGraph && (
              <button
                className={`malpedia-input-graph-toggle${graphOpen ? ' active' : ''}`}
                onClick={onToggleGraph}
                title={graphOpen ? 'Close graph view' : 'Open graph view'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="4" cy="4" r="2" />
                  <circle cx="12" cy="4" r="2" />
                  <circle cx="8" cy="12" r="2" />
                  <line x1="5.5" y1="5.5" x2="7" y2="10.5" />
                  <line x1="10.5" y1="5.5" x2="9" y2="10.5" />
                </svg>
                {hasUnseenGraphData && !graphOpen && (
                  <span className="malpedia-input-graph-toggle-badge" aria-hidden="true" />
                )}
              </button>
            )}
            {subgraphScope !== 'none' && (pinnedNodeCount ?? 0) > 0 && (
              <span className="malpedia-scope-badge">
                Scoped to {pinnedNodeCount} node{pinnedNodeCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {estimatedTokens !== undefined &&
            estimatedTokens > 0 &&
            (maxContextTokens && maxContextTokens > 0 ? (
              <ContextRing tokens={estimatedTokens} maxTokens={maxContextTokens} />
            ) : (
              <span
                className={`malpedia-token-counter${estimatedTokens > 8000 ? ' warn' : ''}`}
                title="Estimated context tokens (1 token ≈ 4 chars)"
              >
                {formatTokens(estimatedTokens)} tokens
              </span>
            ))}
          {streaming && onStop ? (
            <button className="malpedia-stop-btn" onClick={onStop}>
              ■ Stop
            </button>
          ) : (
            <button
              className="malpedia-send-btn"
              onClick={handleSend}
              disabled={disabled}
              title="Send message"
            >
              Send
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
